import React from 'react';
import { Collapse, Table, Tag, Button, Pagination, Dropdown, Menu } from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import { formatDateTime } from '../../utils/timeUtils';

/**
 * 批次审核列表组件
 *
 * 支持两种模式：
 * - pending：显示批量操作下拉菜单、rowSelection，无分页
 * - view：   显示分页、无 rowSelection，标签含状态/提交者/时间详情
 */
const BatchReviewList = ({
  mode,
  batches,
  emptyText = '暂无记录',
  departments,
  getDataTypeLabel,
  buildColumns,
  /* pending-only */
  selectedReviewKeys,
  onSelectionChange,
  reviewing,
  onApproveSelected,
  onRejectSelected,
  /* view-only */
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : `部门ID: ${deptId}`;
  };

  if (!batches || batches.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
        <p>{emptyText}</p>
      </div>
    );
  }

  const isPending = mode === 'pending';

  // view 模式需要分页
  const displayBatches = isPending
    ? batches
    : batches.slice((page - 1) * pageSize, page * pageSize);

  return (
    <>
      {isPending && (
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
          <Dropdown
            overlay={
              <Menu
                onClick={({ key }) => {
                  if (key === 'approve') onApproveSelected();
                  else if (key === 'reject') onRejectSelected();
                }}
              >
                <Menu.Item key="approve" icon={<CheckOutlined />}>通过选中行</Menu.Item>
                <Menu.Item key="reject" icon={<CloseOutlined />}>拒绝选中行</Menu.Item>
              </Menu>
            }
            trigger={['click']}
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
          >
            <Button loading={reviewing}>批量操作 <DownOutlined /></Button>
          </Dropdown>
          <div />
        </div>
      )}

      <Collapse
        defaultActiveKey={[]}
        style={{ background: '#fff' }}
        items={displayBatches.map(batch => ({
          key: batch.key,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'bold' }}>
                  {getDepartmentName(batch.department_id)}
                  &nbsp;-&nbsp;
                  {getDataTypeLabel(batch.data_type, batch.department_id)}
                </span>
                <Tag color="blue">{batch.submissions.length} 条数据</Tag>
                {!isPending && batch.status && (
                  <Tag color={batch.status === 'approved' ? 'green' : batch.status === 'rejected' ? 'red' : 'orange'}>
                    {batch.status === 'approved' ? '已通过' : batch.status === 'rejected' ? '已拒绝' : '待审核'}
                  </Tag>
                )}
                {!isPending && batch.submitted_by_username && (
                  <Tag color="green">提交者: {batch.submitted_by_username}</Tag>
                )}
                {!isPending && batch.submitted_at && (
                  <span style={{ color: '#666', fontSize: '12px' }}>
                    {formatDateTime(batch.submitted_at)}
                  </span>
                )}
              </div>
            </div>
          ),
          children: (
            <div>
              <Table
                columns={buildColumns(batch.submissions)}
                dataSource={batch.submissions}
                rowKey="id"
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="middle"
                bordered
                {...(isPending && onSelectionChange ? {
                  rowSelection: {
                    selectedRowKeys: selectedReviewKeys,
                    onChange: onSelectionChange,
                    getCheckboxProps: (record) => ({
                      disabled: record.status === 'approved' || record.status === 'rejected',
                    }),
                  },
                } : {})}
              />
            </div>
          ),
        }))}
      />

      {!isPending && (
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={batches.length}
            onChange={onPageChange}
            onShowSizeChange={(current, size) => {
              if (onPageSizeChange) onPageSizeChange(size);
              if (onPageChange) onPageChange(1, size);
            }}
            showSizeChanger
            showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个批次`}
            pageSizeOptions={['10', '20', '50', '100']}
          />
        </div>
      )}
    </>
  );
};

export default BatchReviewList;
